import { useTranslation } from "@redotech/redo-i18n";
import { useReturnsSettingsExperience } from "@redotech/redo-merchant-app-common/settings/use-returns-settings-experience";
import { FlowType } from "@redotech/redo-return-core-common/return-flow-enums";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { DisabledTooltip } from "@redotech/redo-web/arbiter-components/tooltip/disabled-tooltip";
import { Flex } from "@redotech/redo-web/flex";
import { TextInput } from "@redotech/redo-web/text-input";
import { memo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { flowEditorClosePath, flowEditorReturnTo } from "./flow-editor-return";

export interface FlowEditorHeaderProps {
  flowType: FlowType;

  showSaveAndCancel: boolean;
  cancelDisabled: boolean;
  saveDisabled: string | undefined;
  savePending: boolean;

  showPublishButton: boolean;
  publishClicked: () => void;
  showUnpublishButton: boolean;
  unpublishClicked: () => void;
  publishPending: boolean;
  /** Tooltip message + disabled state for Publish. Undefined = enabled. */
  publishDisabled?: string;

  rightPanelOpen: boolean;

  detailsClicked: () => void;
  cancelClicked: () => void;
  saveClicked: () => void;

  ruleName: string;
  setRuleName: (name: string) => void;

  defaultButtonClicked?: () => void; // button that is visible when all others are not
  defaultButtonPending?: boolean;
  defaultButtonText?: string;

  showRestoreButton?: boolean;
  restoreClicked?: () => void;
  restorePending?: boolean;

  isNewEditor?: boolean;
  toggleEditorMode?: () => void;
  closeNavigationPath?: string;
}

export const FlowEditorHeaderButtons = memo(function FlowEditorHeaderButtons({
  flowType,
  showSaveAndCancel,
  cancelDisabled,
  saveDisabled,
  savePending,
  publishPending,
  publishDisabled,
  showPublishButton,
  showUnpublishButton,
  rightPanelOpen,
  ruleName,
  setRuleName,
  detailsClicked,
  cancelClicked,
  saveClicked,
  publishClicked,
  unpublishClicked,
  defaultButtonClicked,
  defaultButtonPending,
  defaultButtonText,
  showRestoreButton,
  restoreClicked,
  restorePending,
  isNewEditor,
  toggleEditorMode,
  closeNavigationPath = "/settings/customer-portal",
}: FlowEditorHeaderProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { useNewUi } = useReturnsSettingsExperience();
  const closePath =
    flowEditorReturnTo(location.search) ??
    flowEditorClosePath(closeNavigationPath, useNewUi);
  const ruleSpecificItems = flowType === FlowType.RULE && (
    <>
      <TextInput
        onChange={setRuleName}
        placeholder={t("Give this rule a name")}
        value={ruleName}
      />
      <RedoButton
        hierarchy="tertiary"
        onClick={detailsClicked}
        size="lg"
        text={t("Details")}
      />
    </>
  );

  const showDefaultButton =
    defaultButtonClicked &&
    defaultButtonText &&
    !showSaveAndCancel &&
    !showPublishButton &&
    !showRestoreButton;

  return (
    <Flex>
      {ruleSpecificItems}
      {toggleEditorMode && (
        <RedoButton
          hierarchy="tertiary"
          onClick={toggleEditorMode}
          size="sm"
          text={
            isNewEditor ? t("Switch to old editor") : t("Switch to new editor")
          }
        />
      )}
      {isNewEditor && (
        <RedoButton
          hierarchy="secondary"
          onClick={() => navigate(closePath)}
          size="sm"
          text={t("Close")}
        />
      )}
      {showSaveAndCancel && (
        <>
          <RedoButton
            disabled={cancelDisabled}
            hierarchy="secondary"
            onClick={cancelClicked}
            size="sm"
            text={t("Cancel")}
          />
          <DisabledTooltip disabledMessage={saveDisabled}>
            <RedoButton
              disabled={!!saveDisabled}
              hierarchy="primary"
              onClick={saveClicked}
              pending={savePending}
              size="sm"
              text={t("Save")}
            />
          </DisabledTooltip>
        </>
      )}
      {showUnpublishButton && (
        <RedoButton
          hierarchy="secondary"
          onClick={unpublishClicked}
          pending={publishPending}
          size="sm"
          text={t("Unpublish")}
        />
      )}
      {showPublishButton && (
        <DisabledTooltip disabledMessage={publishDisabled}>
          <RedoButton
            dataTestId="publish-flow-button"
            disabled={!!publishDisabled}
            hierarchy="primary"
            onClick={publishClicked}
            pending={publishPending}
            size="sm"
            text={t("Publish")}
            theme="success"
          />
        </DisabledTooltip>
      )}
      {showRestoreButton && restoreClicked && (
        <RedoButton
          hierarchy="primary"
          onClick={restoreClicked}
          pending={restorePending ?? false}
          size="sm"
          text={t("Restore")}
        />
      )}
      {showDefaultButton && (
        <RedoButton
          hierarchy="secondary"
          onClick={defaultButtonClicked}
          pending={defaultButtonPending ?? false}
          size="sm"
          text={defaultButtonText}
        />
      )}
    </Flex>
  );
});
